// Start einer Plattform (Besitzer: geruest, Integration M1): Persona aus ?as=, Server-Konfiguration
// (Auth-Modus, APP_TODAY) und Registry laden, Router bauen, rendern. Die Plattform-Einstiege
// src/platform/<p>/main.tsx (ui-basis) rufen nur startPlatform() auf.
import '@web/styles/app.css';
import { configureFormat } from '@bar/ui/basis';
import { type QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MotionConfig } from 'motion/react';
import { type ComponentType, type ReactNode, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, type RouteObject, RouterProvider } from 'react-router';
import { AuthProvider, coreApi, coreKeys, initPersonaFromUrl, useViewerFromAuth } from '@web/lib';
import { loadContributions, type LoadedRegistry } from '@web/registry/load';
import { RegistryProvider } from '@web/registry/context';
import { buildRoutes } from '@web/registry/router';
import type { PlatformKey } from '@web/registry/types';
import { detectBasename } from './basename';
import { createQueryClient } from './query-client';

const PREFIX: Record<PlatformKey, '' | '/admin' | '/ai'> = {
  people: '',
  admin: '/admin',
  ai: '/ai',
};

/** ANNAHME: Locale en-GB (DESIGN §4.8). */
export const APP_LOCALE = 'en-GB';

export interface StartPlatformOptions {
  platform: PlatformKey;
  Shell: ComponentType;
  builtinPublicRoutes?: RouteObject[];
}

/**
 * Lädt die Auth-Konfiguration vor dem ersten Render in den Query-Cache (AuthProvider nutzt sie) und
 * stellt die Formatierer auf das Server-Datum (APP_TODAY, AUFTRAG §3 „Uhr“). Ohne Antwort bleibt es
 * beim Browser-Datum; der AuthProvider zeigt dann seinen Fehlerzustand.
 */
export async function prepareFormat(queryClient: QueryClient): Promise<void> {
  configureFormat({ locale: APP_LOCALE });
  try {
    const config = await queryClient.fetchQuery({
      queryKey: coreKeys.authConfig,
      queryFn: coreApi.authConfig,
      staleTime: Infinity,
    });
    configureFormat({ locale: APP_LOCALE, today: config.today });
  } catch {
    // AuthProvider fragt erneut und zeigt den Fehler; Formatierer nutzen das Browser-Datum.
  }
}

/** Registry mit dem Viewer aus der Sitzung (/api/core/me) statt aus der URL. */
export function SessionRegistry({
  registry,
  children,
}: {
  registry: LoadedRegistry<PlatformKey>;
  children: ReactNode;
}) {
  const viewer = useViewerFromAuth();
  return (
    <RegistryProvider registry={registry} viewer={viewer}>
      {children}
    </RegistryProvider>
  );
}

export async function startPlatform({
  platform,
  Shell,
  builtinPublicRoutes,
}: StartPlatformOptions) {
  const container = document.getElementById('root');
  if (!container) throw new Error('#root fehlt');
  document.documentElement.dataset.platform = platform;

  // Demo: `?as=<persona>` für diesen Tab merken (API-Client und SSE senden sie mit).
  initPersonaFromUrl();
  const queryClient = createQueryClient();
  const [registry] = await Promise.all([loadContributions(platform), prepareFormat(queryClient)]);
  const router = createBrowserRouter(buildRoutes(registry, { Shell, builtinPublicRoutes }), {
    basename: detectBasename(PREFIX[platform]) || undefined,
  });

  createRoot(container).render(
    <StrictMode>
      <MotionConfig reducedMotion="user">
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <SessionRegistry registry={registry}>
              <RouterProvider router={router} />
            </SessionRegistry>
          </AuthProvider>
        </QueryClientProvider>
      </MotionConfig>
    </StrictMode>,
  );
}
